import fetch from 'unfetch';
import { logger } from '@immediate_media/logger';

export type UserInfo = {
  email: string;
  idToken: string;
  idp: string;
  role: string | string[] | null;
  userId: string;
  vanityId: string | string[];
};

/**
 * Get user info from the server. The `fields` argument is required and the endpoint will return
 * only the values corresponding to those fields.
 *
 * @param fields The fields to fetch provided as a string or array of strings.
 */
const getUserInfo = async <TField extends keyof UserInfo>(
  fields: TField | Array<TField>,
): Promise<Record<TField, UserInfo[TField]> | null> => {
  if (!fields) {
    throw new Error('The `fields` argument is required to fetch user info.');
  }

  try {
    const fieldsArray = Array.isArray(fields) ? fields : [fields];
    const params = new URLSearchParams(fieldsArray.map((field) => ['fields', field]));
    const response = await fetch(`/auth/user-info?${params.toString()}`);

    if (!response.ok) {
      const { status, statusText } = response;

      logger.error(`Failed to fetch user info. ${status} - ${statusText}`);

      return null;
    }

    type UserInfoPartial = Record<TField, UserInfo[TField]>;

    const data = (await response.json()) as UserInfoPartial;

    const failedFields = fieldsArray.filter((field) => {
      // Skip `role` since `null` is a valid return value for that field.
      if (field === 'role') {
        return false;
      }

      return !data?.[field];
    });

    if (failedFields.length) {
      logger.error(`Failed to fetch user info. Missing fields: ${failedFields.join(', ')}.`);

      return null;
    }

    return data;
  } catch (error) {
    logger.error(`Failed to fetch user info. ${error instanceof Error ? error.message : ''}`, error);

    return null;
  }
};

export default getUserInfo;
